import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import Logo from "./Logo";

const navItems = [
  { key: "home", path: "/" },
  { key: "services", path: "/services" },
  { key: "about", path: "/about" },
  { key: "contact", path: "/contact" },
];

function LangIconButton({ onClick, lang, testId, ariaLabel }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      aria-label={ariaLabel}
      title={ariaLabel}
      className="relative inline-flex items-center justify-center w-11 h-11 border border-[rgba(0,240,255,0.25)] text-[#00F0FF] hover:border-[#00F0FF] hover:bg-[rgba(0,240,255,0.08)] hover:shadow-[0_0_18px_rgba(0,240,255,0.35)] transition-all"
    >
      <Globe size={18} strokeWidth={2} />
      <span className="absolute -bottom-1 -end-1 bg-[#00F0FF] text-[#050B14] text-[9px] font-bold font-mono-tech px-1 leading-tight tracking-wide">
        {lang === "ar" ? "EN" : "AR"}
      </span>
    </button>
  );
}

export default function Navbar() {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // close mobile drawer on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-[#050B14]/85 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 h-20 sm:h-24 flex items-center justify-between gap-4">
        <Logo />

        <nav className="hidden lg:flex items-center gap-7">
          {navItems.map((n) => (
            <NavLink
              key={n.path}
              to={n.path}
              end={n.path === "/"}
              data-testid={`nav-link-${n.key}`}
              className={({ isActive }) =>
                `text-[13px] transition-colors font-medium ${
                  isActive
                    ? "text-[#00F0FF]"
                    : "text-slate-200 hover:text-[#00F0FF]"
                }`
              }
            >
              {t.nav[n.key]}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <LangIconButton
            onClick={toggle}
            lang={lang}
            testId="lang-toggle-btn"
            ariaLabel={t.common.switch_lang}
          />

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 border border-white/10 text-white hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* mobile drawer */}
      {open && (
        <div
          className="lg:hidden border-t border-white/5 bg-[#050B14]/95 backdrop-blur-xl"
          data-testid="mobile-nav"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {navItems.map((n) => (
              <NavLink
                key={n.path}
                to={n.path}
                end={n.path === "/"}
                data-testid={`mobile-nav-link-${n.key}`}
                className={({ isActive }) =>
                  `text-base font-medium ${
                    isActive ? "text-[#00F0FF]" : "text-slate-100"
                  }`
                }
              >
                {t.nav[n.key]}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
